import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  Paper,
  Divider,
  Alert,
  Snackbar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import axios from 'axios';
import { logAction, formatReportNumber, LOG_ACTIONS } from '../../utils/logClient';

const emptyParsed = {
  faits: [],
  personnesPhysiques: [],
  personnesMorales: [],
  vehicules: [],
  objets: []
};

const deepEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const addUnique = (list, item) => {
  if (list.some(existing => deepEqual(existing, item))) return list;
  return [...list, item];
};

const normalize = (str = '') => str.replace(/\r/g, '').trim();

const cleanEmptyValue = (val = '') => {
  const trimmed = (val || '').replace(/\s+/g, ' ').trim();
  if (!trimmed) return '';
  const compact = trimmed.replace(/[,\s]/g, '');
  if (!compact) return '';
  return trimmed.replace(/\s+,/g, ', ').replace(/,\s+/g, ', ').trim();
};

const splitAddressParts = (raw = '') => {
  let full = (raw || '').trim();
  let country = '';
  let destination = '';

  // Extraire destination entre parenthèses (en fin ou en milieu)
  let match = full.match(/\(([^()]*)\)\s*$/);
  if (match) {
    destination = match[1].trim();
    full = full.slice(0, match.index).trim();
  } else {
    match = full.match(/\(([^()]*)\)/);
    if (match) {
      destination = match[1].trim();
      full = (full.slice(0, match.index) + full.slice(match.index + match[0].length)).trim();
    }
  }

  // Nettoyer virgule finale
  full = full.replace(/,+\s*$/, '').trim();

  // Chercher pays au début, séparé par virgule
  const countryMatch = full.match(/^([^,]+),\s*(.*)$/);
  if (countryMatch) {
    country = countryMatch[1].trim();
    full = countryMatch[2].trim();
  }

  // Nettoyer double espaces
  full = full.replace(/\s{2,}/g, ' ');

  return { country, address: full, destination };
};

const parseDateTimeString = (raw = '') => {
  const cleaned = raw
    .replace(/^[^0-9]*le\s+/i, '')
    .replace(/^[^0-9]*du\s+/i, '')
    .replace(/^[^0-9]*op\s+/i, '')
    .replace(/h\.?|uur/gi, '')
    .trim();
  const match = cleaned.match(/([0-9]{2})\/([0-9]{2})\/([0-9]{4})(?:\s*[-–]?\s*([0-9]{2}:[0-9]{2}))?/);
  if (!match) return '';
  const [, day, month, year, time] = match;
  const t = time || '00:00';
  return `${year}-${month}-${day}T${t}`;
};

const normalizeBirthdateString = (raw = '') => {
  const match = (raw || '').trim().match(/^([0-9]{2})\/([0-9]{2})\/([0-9]{4})$/);
  if (match) {
    const [, day, month, year] = match;
    return `${year}-${month}-${day}`;
  }
  return raw || '';
};

const splitBlocks = (text) => {
  const merged = normalize(text).replace(
    /(\S[^\n]*)\n\s*\n\s*(?=(marque\s*:|merk\s*:|no\s+de\s+plaque|nummerplaat|no\s+de\s+ch[aâ]ssis|chassisnr))/gi,
    '$1\n'
  );
  return merged
    .split(/\n\s*\n+/)
    .map(block => block.trim())
    .filter(Boolean);
};

const parseFait = (block) => {
  const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
  if (!lines.length) return null;

  const firstLine = lines[0];
  const isUndetermined = /fait\s+non\s+déterminé/i.test(block) || /onbepaald\s+feit/i.test(block);
  if (isUndetermined) {
    return {
      isUndetermined: true,
      isQualified: false,
      isTentative: false,
      fait: '',
      article: '',
      timeType: 'fixed',
      fixedDateTime: '',
      rangeLowDateTime: '',
      rangeHighDateTime: '',
      address: '',
      destination: ''
    };
  }

  let fait = firstLine;
  let isTentative = false;
  let isQualified = false;

  if (/^tentative\s+de/i.test(fait)) {
    isTentative = true;
    fait = fait.replace(/^tentative\s+de\s*/i, '').trim();
  }

  if (/^fait\s+qualifié\s+de/i.test(fait)) {
    isQualified = true;
    fait = fait.replace(/^fait\s+qualifié\s+de\s*/i, '').trim();
  }

  // Article
  const articleLine = lines.find(l => /article/i.test(l) || /artikel/i.test(l));
  const article = articleLine ? articleLine.split(':').slice(1).join(':').trim() : '';

  // Temps
  let timeType = 'fixed';
  let fixedDateTime = '';
  let rangeLowDateTime = '';
  let rangeHighDateTime = '';

  const rangeLine = lines.find(l => /^entre\s+/i.test(l));
  if (rangeLine) {
    timeType = 'range';
    const match = rangeLine.match(/entre\s+(.+?)\s+et\s+(.+)/i);
    if (match) {
      rangeLowDateTime = parseDateTimeString(match[1].trim().replace(/^-/, '').trim());
      rangeHighDateTime = parseDateTimeString(match[2].trim().replace(/^-/, '').trim());
    }
  } else {
    const fixedLine = lines.find(l => /^du\s*:|^du\s+/i.test(l) || /^op\s*:|^op\s+/i.test(l));
    if (fixedLine) {
      const fixedRaw = fixedLine.split(':').slice(1).join(':').trim() || fixedLine.replace(/^(du|op)\s*/i, '').trim();
      fixedDateTime = parseDateTimeString(fixedRaw);
    }
  }

  // Adresse
  const addressLine = lines.find(l => /^a\s*:|^à\s*:|^te\s*:|^a\s+/i.test(l));
  const addressRaw = addressLine ? addressLine.split(':').slice(1).join(':').trim() || addressLine.replace(/^(a|à|te)\s*/i, '').trim() : '';
  const { country, address, destination } = splitAddressParts(addressRaw);

  return {
    isUndetermined: false,
    isQualified,
    isTentative,
    fait,
    article,
    timeType,
    fixedDateTime,
    rangeLowDateTime,
    rangeHighDateTime,
    country,
    address,
    destination
  };
};

const parsePersonnePhysique = (block) => {
  const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
  if (!lines.length) return null;

  const firstLine = lines[0];
  const unknownMatch = firstLine.match(/^(.+?)\s+inconnu/i) || firstLine.match(/^(.+?)\s+onbekend/i);
  let qualite = '';
  let lastname = '';
  let namePart = '';
  let isUnknown = false;

  if (unknownMatch) {
    qualite = unknownMatch[1].trim();
    isUnknown = true;
  } else {
    const firstMatch = firstLine.match(/^(.+?)\s+([A-ZÀ-Ÿ0-9' -]+)\s+(.+)$/u);
    if (!firstMatch) return null;
    qualite = firstMatch[1].trim();
    lastname = firstMatch[2].trim();
    namePart = firstMatch[3].trim();
    isUnknown = /inconnu/i.test(namePart) || /inconnu/i.test(lastname) || /onbekend/i.test(namePart);
  }

  let firstname = null;
  let otherFirstnames = null;

  if (!isUnknown) {
    const nameParts = namePart.split(',').map(p => p.trim()).filter(Boolean);
    firstname = nameParts.shift() || '';
    otherFirstnames = nameParts.join(', ');
  }

  const genderLine = lines.find(l => /^sexe\s*:/i.test(l) || /^geslacht\s*:/i.test(l));
  let gender = '';
  if (genderLine) {
    const val = genderLine.split(':').slice(1).join(':').toLowerCase();
    if (val.includes('masc') || val.includes('mann')) gender = 'M';
    if (val.includes('fém') || val.includes('vrouw')) gender = 'F';
  }

  const birthLine = lines.find(l => /(né|née)\s+à/i.test(l));
  let birthPlace = '';
  let birthdate = '';
  if (birthLine) {
    const placeMatch = birthLine.match(/né[e]?\s+à\s+(.+?)\s+le\s+([0-9]{2}\/[0-9]{2}\/[0-9]{4})/i);
    if (placeMatch) {
      birthPlace = placeMatch[1].trim();
      birthdate = placeMatch[2].trim();
    } else {
      const placeOnly = birthLine.replace(/né[e]?\s+à\s*/i, '').trim();
      birthPlace = placeOnly;
    }
  }

  birthdate = normalizeBirthdateString(birthdate);

  const civilStatusLine = lines.find(l => /etat civil/i.test(l) || /burgerlijke stand/i.test(l));
  const civilStatus = civilStatusLine ? civilStatusLine.split(':').slice(1).join(':').trim() : '';

  const nationalityLine = lines.find(l => /nationalité/i.test(l) || /nationaliteit/i.test(l));
  let nationality = nationalityLine ? nationalityLine.split(':').slice(1).join(':').trim() : '';
  nationality = nationality.replace(/n[°º]?\s*sp.*$/i, '').trim();

  const professionLine = lines.find(l => /profession/i.test(l) || /beroep/i.test(l));
  const profession = professionLine ? professionLine.split(':').slice(1).join(':').trim() : '';

  const residenceLine = lines.find(l => /domicili[ée]e?/i.test(l) || /domicilie[e]?/i.test(l) || /wonende/i.test(l));
  const residenceRaw = residenceLine
    ? residenceLine.split(':').slice(1).join(':').trim() ||
      residenceLine.replace(/^(domicili[ée]e?|domicilie[e]?|wonende)\s*(à|a|te)?/i, '').trim()
    : '';
  const residence = cleanEmptyValue(residenceRaw);

  const nationalNumberLine = lines.find(l => /numéro national/i.test(l));
  const nationalNumber = nationalNumberLine ? nationalNumberLine.split(':').slice(1).join(':').trim() : '';

  const raavisLine = lines.find(l => /raavis/i.test(l));
  const raavisNumber = raavisLine ? raavisLine.split(':').slice(1).join(':').trim() : '';

  return {
    qualite,
    additionalQualities: [],
    isUnknown,
    lastname: isUnknown ? null : lastname,
    firstname: isUnknown ? null : firstname,
    otherFirstnames: isUnknown ? null : (otherFirstnames || ''),
    gender: isUnknown ? null : gender,
    birthPlace: isUnknown ? null : birthPlace,
    birthdate: isUnknown ? null : birthdate,
    civilStatus: isUnknown ? null : civilStatus,
    nationality: isUnknown ? null : nationality,
    profession: isUnknown ? null : profession,
    residence: isUnknown ? null : (residence || null),
    nationalNumber: isUnknown ? null : nationalNumber,
    raavisNumber: isUnknown ? null : raavisNumber,
    photographies: '',
    empreintes: '',
    noticeIndividuelle: ''
  };
};

const parsePersonneMorale = (block) => {
  const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
  if (!lines.length) return null;

  const firstLine = lines[0];
  const cleanedFirst = firstLine.replace(/\s+/g, ' ').trim();
  const firstWithoutSise = cleanedFirst.split(/sise\s+à|sise\s+a|sise\s*:|sise\s+:/i)[0].trim();

  let qualite = '';
  let denomination = '';
  const firstMatch = firstWithoutSise.match(/^(.+?)\s+([A-Z0-9].+)$/);
  if (firstMatch) {
    qualite = firstMatch[1].trim();
    denomination = firstMatch[2].trim();
  } else {
    const parts = firstWithoutSise.split(/\s+/);
    qualite = parts.shift() || '';
    denomination = parts.join(' ').trim();
  }

  let companyNumber = '';
  let headquarters = '';

  lines.forEach(line => {
    if (/numéro\s+tva/i.test(line)) {
      companyNumber = line.split(':').slice(1).join(':').trim();
    } else if (/numéro\s*r\.?c/i.test(line) || /numéro\s*rc/i.test(line)) {
      if (!companyNumber) {
        companyNumber = line.split(':').slice(1).join(':').trim();
      }
    } else if (/siège\s+social/i.test(line) || /sise\s+à/i.test(line)) {
      const parts = line.split(':');
      headquarters = parts.slice(1).join(':').trim() || line.replace(/^(siège social|sise à)\s*/i, '').trim();
    }
  });

  return {
    qualite,
    denomination,
    companyNumber,
    headquarters: cleanEmptyValue(headquarters)
  };
};

const parseVehicule = (block) => {
  const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
  if (!lines.length) return null;

  let category = lines[0].trim();
  if (/^marque\s*:|^merk\s*:/i.test(category)) {
    category = 'Véhicule';
  }
  let brand = '';
  let plateNumber = '';
  let chassisNumber = '';
  let nationality = '';
  let color = '';
  let insurance = '';
  let technicalControl = '';

  lines.forEach(line => {
    if (/^marque\s*:|^merk\s*:/i.test(line)) {
      brand = line.split(':').slice(1).join(':').trim();
    } else if (/plaque/i.test(line) || /nummerplaat/i.test(line)) {
      plateNumber = line.split(':').slice(1).join(':').trim();
    } else if (/châssis|chassisnr/i.test(line)) {
      chassisNumber = line.split(':').slice(1).join(':').trim();
    } else if (/nationalit/i.test(line) || /nationaliteit/i.test(line)) {
      nationality = line.split(':').slice(1).join(':').trim();
    } else if (/couleur|kleur/i.test(line)) {
      color = line.split(':').slice(1).join(':').trim();
    } else if (/assurance|verzekering/i.test(line)) {
      insurance = line.split(':').slice(1).join(':').trim();
    } else if (/valable\s+jusque|geldig/i.test(line)) {
      technicalControl = line.split(':').slice(1).join(':').trim();
    }
  });

  if (!brand && lines.length > 1) {
    // parfois la catégorie est le seul titre, la marque suit sur la 2e ligne
    const secondLine = lines[1];
    if (/marque/i.test(secondLine)) {
      brand = secondLine.split(':').slice(1).join(':').trim();
    }
  }

  if (!brand) {
    brand = category;
  }

  return {
    brand,
    category,
    plateNumber: plateNumber.toUpperCase(),
    chassisNumber: chassisNumber.toUpperCase(),
    nationality,
    color,
    insurance,
    technicalControl
  };
};

const parseObjet = (block) => {
  const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
  if (!lines.length) return null;

  const title = lines[0];
  const details = [];

  lines.slice(1).forEach(line => {
    const parts = line.split(':');
    if (parts.length > 1) {
      const key = parts[0].trim();
      const val = parts.slice(1).join(':').trim();
      if (val) {
        details.push(`${key} ${val}`);
      }
    } else if (line) {
      details.push(line);
    }
  });

  const description = [title, ...details].filter(Boolean).join(' - ').trim();

  return {
    description,
    sinNumber: ''
  };
};

const guessType = (block) => {
  const firstLine = block.split('\n').find(Boolean) || '';
  const firstTrim = firstLine.trim();
  if (/sexe\s*:|état civil|etat civil|domicili[ée]|né\s+à|née\s+à|nationalité|nationaliteit/i.test(block)) {
    return 'personnePhysique';
  }
  if (/marque\s*:|no de plaque|nummerplaat|châssis|chassisnr|assurance|verzekering/i.test(block)) {
    return 'vehicule';
  }
  if (/^(moto|voiture|personenauto|camionnette|camionette|tracteur|remorque|cyclomoteur|voiture\s+voyageur|lichte\s+vrachtauto|remorque)/i.test(firstTrim)) {
    return 'vehicule';
  }
  if (/numéro\s+tva|numéro\s*r\.?c|sise\s+à|siège\s+social|préjudicié/i.test(block)) {
    return 'personneMorale';
  }
  if (
    /article|fait\s+qualifié|tentative\s+de|fait\s+non\s+déterminé|entre le|entre\s+le|entre\s+la/i.test(block) ||
    /(^|\n)\s*(du\s*:|du\s+|op\s*:|op\s+)/i.test(block) // ex: "Du : 23/10/2025 - 20:00"
  ) {
    return 'fait';
  }
  return 'objet';
};

const parseBlocks = (text) => {
  const blocks = splitBlocks(text);
  let result = { ...emptyParsed };

  blocks.forEach(block => {
    const type = guessType(block);
    let parsedItem = null;
    switch (type) {
      case 'fait':
        parsedItem = parseFait(block);
        if (parsedItem) result.faits = addUnique(result.faits, parsedItem);
        break;
      case 'personnePhysique':
        parsedItem = parsePersonnePhysique(block);
        if (parsedItem) result.personnesPhysiques = addUnique(result.personnesPhysiques, parsedItem);
        break;
      case 'personneMorale':
        parsedItem = parsePersonneMorale(block);
        if (parsedItem) result.personnesMorales = addUnique(result.personnesMorales, parsedItem);
        break;
      case 'vehicule':
        parsedItem = parseVehicule(block);
        if (parsedItem) result.vehicules = addUnique(result.vehicules, parsedItem);
        break;
      default:
        parsedItem = parseObjet(block);
        if (parsedItem) result.objets = addUnique(result.objets, parsedItem);
        break;
    }
  });

  return result;
};

const copyButtonStyle = {
  px: 1,
  py: 0.25,
  border: '1px solid #8a8a8a',
  borderRadius: '3px',
  boxShadow: 'inset 1px 1px 0 #fff, inset -1px -1px 0 #b5b5b5',
  bgcolor: '#dcdcdc',
  color: '#000',
  fontSize: '0.85em',
  fontWeight: 600,
  mx: 0.25
};

const ItemCard = ({ typeLabel, summary, onRemove }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.25,
      mb: 1,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1
    }}
  >
    <Box sx={{ flex: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {typeLabel}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {summary}
      </Typography>
    </Box>
    <IconButton size="small" onClick={onRemove} aria-label="Supprimer">
      <DeleteIcon fontSize="small" />
    </IconButton>
  </Paper>
);

function ImportISLP({ open, onClose, report, reloadReportData }) {
  const [input, setInput] = useState('');
  const [parsed, setParsed] = useState(emptyParsed);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    if (!open) {
      // reset quand le dialog est fermé
      setInput('');
      setParsed(emptyParsed);
      setBusy(false);
      setError('');
    }
  }, [open]);

  const hasResults = useMemo(() => {
    return Object.values(parsed).some(arr => arr.length > 0);
  }, [parsed]);

  const mergeParsed = (newData) => {
    setParsed(prev => ({
      faits: [...prev.faits, ...newData.faits.filter(item => !prev.faits.some(p => deepEqual(p, item)))],
      personnesPhysiques: [...prev.personnesPhysiques, ...newData.personnesPhysiques.filter(item => !prev.personnesPhysiques.some(p => deepEqual(p, item)))],
      personnesMorales: [...prev.personnesMorales, ...newData.personnesMorales.filter(item => !prev.personnesMorales.some(p => deepEqual(p, item)))],
      vehicules: [...prev.vehicules, ...newData.vehicules.filter(item => !prev.vehicules.some(p => deepEqual(p, item)))],
      objets: [...prev.objets, ...newData.objets.filter(item => !prev.objets.some(p => deepEqual(p, item)))]
    }));
  };

  const handleTranslate = () => {
    setError('');
    const newParsed = parseBlocks(input);
    mergeParsed(newParsed);
  };

  const handleRemove = (collection, index) => {
    setParsed(prev => ({
      ...prev,
      [collection]: prev[collection].filter((_, i) => i !== index)
    }));
  };

  const handleImport = async () => {
    if (!report?._id) {
      setError('Rapport introuvable.');
      return;
    }
    if (!hasResults) {
      setError('Aucune donnée à importer.');
      return;
    }

    try {
      setBusy(true);
      setError('');
      const token = sessionStorage.getItem('authToken');
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      const reportId = report._id;

      const postAll = async (items, endpoint) => {
        for (const item of items) {
          await axios.post(`/api/report/${reportId}${endpoint}`, item, { headers, withCredentials: true });
        }
      };

      await postAll(parsed.faits, '/faits');
      await postAll(parsed.personnesPhysiques, '/personnes-physiques');
      await postAll(parsed.personnesMorales, '/personnes-morales');
      await postAll(parsed.vehicules, '/vehicules');
      await postAll(parsed.objets, '/objets');

      // Log de l'import ISLP avec toutes les données importées
      logAction(LOG_ACTIONS.ELEMENTS_IMPORT, formatReportNumber(report.number, report.year), {
        faits: parsed.faits,
        personnesPhysiques: parsed.personnesPhysiques,
        personnesMorales: parsed.personnesMorales,
        vehicules: parsed.vehicules,
        objets: parsed.objets,
        totalElements: parsed.faits.length + parsed.personnesPhysiques.length + 
                       parsed.personnesMorales.length + parsed.vehicules.length + parsed.objets.length
      });

      await reloadReportData?.();
      setSnackbar('Importation réalisée');
      onClose();
    } catch (err) {
      console.error('Erreur import ISLP', err);
      setError('Erreur lors de l’import. Vérifiez les données puis réessayez.');
    } finally {
      setBusy(false);
      setParsed(emptyParsed);
      setInput('');
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={busy ? undefined : onClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Importer les encodages depuis votre ISLP
          <IconButton onClick={onClose} disabled={busy} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Paper
              variant="outlined"
              sx={{ p: 1.5, bgcolor: 'action.hover' }}
            >
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                1. Rendez-vous dans votre <strong>ISLP</strong>, dans les onglets <strong>"Faits"</strong>, <strong>"Personnes physiques"</strong>, <strong>"Personnes morales"</strong>, <strong>"Véhicules"</strong> et <strong>"Objets"</strong>.
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                2. Sélectionnez un élément puis cliquez sur le bouton{' '}
                <Box component="span" sx={copyButtonStyle}>
                  Copier
                </Box>
                OU sur{' '}
                <Box component="span" sx={copyButtonStyle}>
                  Copier tout
                </Box>.
              </Typography>
              <Typography variant="body2">
                3. Collez votre presse-papier ici et cliquez sur "<strong>Traduire les données</strong>".
              </Typography>
            </Paper>
            <TextField
              label="Coller les données ISLP"
              placeholder="Collez ici les données brutes..."
              multiline
              minRows={6}
              fullWidth
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
            <Button
              variant="contained"
              onClick={handleTranslate}
              disabled={!input.trim()}
              endIcon={<ArrowDownwardIcon />}
              sx={{ alignSelf: 'center' }}
            >
              Traduire les données
            </Button>

            <Divider />

            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: 'text.secondary' }}
            >
              Données traduites pour BlueNote
            </Typography>

            {!hasResults && (
              <Typography variant="body2" color="text.secondary">
                Aucune donnée traduite pour le moment.
              </Typography>
            )}

            {parsed.faits.map((item, idx) => {
              const summary = item.isUndetermined
                ? 'Fait non déterminé - PV subséquent'
                : [
                    item.fait,
                    item.article ? `Article: ${item.article}` : '',
                    item.timeType === 'range'
                      ? `Entre: ${item.rangeLowDateTime || '...'} / ${item.rangeHighDateTime || '...'}`
                      : (item.fixedDateTime ? `Du: ${item.fixedDateTime}` : ''),
                    (item.country || item.address || item.destination)
                      ? `Lieu: ${item.country || ''}${item.country && item.address ? ', ' : ''}${item.address || ''}${item.destination ? ` (${item.destination})` : ''}`
                      : '',
                    item.isTentative ? 'Tentative' : '',
                    item.isQualified ? 'Fait qualifié' : ''
                  ].filter(Boolean).join(' | ');
              return (
                <ItemCard
                  key={`fait-${idx}`}
                  typeLabel="Fait"
                  summary={summary}
                  onRemove={() => handleRemove('faits', idx)}
                />
              );
            })}

            {parsed.personnesPhysiques.map((item, idx) => {
              const name = item.isUnknown
                ? 'Inconnu(e)'
                : [item.lastname, item.firstname, item.otherFirstnames].filter(Boolean).join(' ');
              const summary = [
                `${item.qualite} - ${name}`.trim(),
                item.gender ? `Sexe: ${item.gender}` : '',
                item.birthPlace ? `Naissance: ${item.birthPlace}${item.birthdate ? ` (${item.birthdate})` : ''}` : (item.birthdate || ''),
                item.nationality ? `Nationalité: ${item.nationality}` : '',
                item.residence ? `Adresse: ${item.residence}` : '',
                item.profession ? `Profession: ${item.profession}` : ''
              ].filter(Boolean).join(' | ');

              return (
                <ItemCard
                  key={`pp-${idx}`}
                  typeLabel="Personne physique"
                  summary={summary}
                  onRemove={() => handleRemove('personnesPhysiques', idx)}
                />
              );
            })}

            {parsed.personnesMorales.map((item, idx) => {
              const summary = [
                `${item.qualite} - ${item.denomination}`.trim(),
                item.companyNumber ? `N°: ${item.companyNumber}` : '',
                item.headquarters ? `Siège: ${item.headquarters}` : ''
              ].filter(Boolean).join(' | ');

              return (
                <ItemCard
                  key={`pm-${idx}`}
                  typeLabel="Personne morale"
                  summary={summary}
                  onRemove={() => handleRemove('personnesMorales', idx)}
                />
              );
            })}

            {parsed.vehicules.map((item, idx) => {
              const summary = [
                `${item.category || 'Véhicule'} - ${item.brand}`.trim(),
                item.plateNumber ? `Plaque: ${item.plateNumber}` : '',
                item.chassisNumber ? `Châssis: ${item.chassisNumber}` : '',
                item.color ? `Couleur: ${item.color}` : '',
                item.nationality ? `Nationalité: ${item.nationality}` : ''
              ].filter(Boolean).join(' | ');

              return (
                <ItemCard
                  key={`veh-${idx}`}
                  typeLabel="Véhicule"
                  summary={summary}
                  onRemove={() => handleRemove('vehicules', idx)}
                />
              );
            })}

            {parsed.objets.map((item, idx) => (
              <ItemCard
                key={`obj-${idx}`}
                typeLabel="Objet"
                summary={item.description}
                onRemove={() => handleRemove('objets', idx)}
              />
            ))}

            {error && (
              <Alert severity="error">
                {error}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={busy || !hasResults}
          >
            Importer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={2500}
        onClose={() => setSnackbar('')}
        message={snackbar}
      />
    </>
  );
}

export default ImportISLP;